import { Box, Flex, Link, Spacer, Text, useMediaQuery } from '@chakra-ui/react';
import {
  ListingAnnouncementFragment,
  MlsListingAgentObject,
  MlsListingComplianceObject,
  PublicListingFragment,
  UserObject,
} from '@client/graphql/__generated__/types';
import { checkIsValidPhoneNumber } from '@common/utils/phoneNumber';
import { MailIcon } from '~/common/icons/MailIcon';
import { TelephoneIcon } from '~/common/icons/TelephoneIcon';
import { UserAvatar } from '../../User/UserAvatar';
import { StorefrontCard } from '../StorefrontCard';

interface ListingAgentCardProps {
  listing: PublicListingFragment;
  listingAnnouncements: ListingAnnouncementFragment[];
  onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

export const ListingAgentCard = ({
  listing,
  listingAnnouncements,
  onLinkClick,
}: ListingAgentCardProps) => {
  return (
    <StorefrontCard
      linkText="View all announcements"
      title="Meet the listing agent"
      onLinkClick={onLinkClick}
    >
      <Flex direction="column" gap={6}>
        <ListingAgentInformation listing={listing} />
        <ListingAgentMostRecentAnnouncement
          message={
            listingAnnouncements && listingAnnouncements.length > 0
              ? listingAnnouncements[0].message
              : ''
          }
        />
      </Flex>
    </StorefrontCard>
  );
};

interface ListingAgentInformationProps {
  listing: PublicListingFragment;
}

export const ListingAgentInformation = ({
  listing,
}: ListingAgentInformationProps) => {
  const [isPrint] = useMediaQuery('print');
  const iconSize = {
    width: '14px',
    height: '14px',
  };

  const { fullName, email, phoneNumber, attributionContact } =
    agentContactInfoFromListing(listing);

  return (
    <Flex alignItems="center" direction="row" gap={{ base: 1, md: 3 }} mx={0}>
      <Flex>
        <UserAvatar
          size={isPrint ? 'md' : { base: 'sm', md: 'md' }}
          user={listing.listingAgent?.user as UserObject}
        />
      </Flex>
      <Flex direction="column" justifyContent="center" minWidth={0}>
        <Text
          fontSize={{ base: 'sm', md: 'md' }}
          fontWeight="500"
          lineHeight={1.2}
        >
          {fullName}
        </Text>
        <Flex color="whiteAlpha.600" direction="column" minWidth={0}>
          <Text
            fontSize={{ base: 'xs', md: 'sm' }}
            lineHeight={1.2}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {listing.mlsListing?.office?.name}
          </Text>
          {!email && !phoneNumber && attributionContact && (
            <Text lineHeight="1">{attributionContact}</Text>
          )}
        </Flex>
      </Flex>
      <Spacer />
      <Flex alignItems="center" color="whiteAlpha.700" direction="row" gap={2}>
        {email && (
          <Link
            href={`mailto:${email ?? ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <MailIcon {...iconSize} />
          </Link>
        )}
        {phoneNumber && (
          <Link
            href={`tel:${phoneNumber ?? ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <TelephoneIcon {...iconSize} />
          </Link>
        )}
      </Flex>
    </Flex>
  );
};

interface ListingAgentMostRecentAnnouncementProps {
  message: string;
}

const ListingAgentMostRecentAnnouncement = ({
  message,
}: ListingAgentMostRecentAnnouncementProps) => {
  return (
    <Box
      backgroundColor="card.bgSubtle"
      borderRadius="sm"
      minHeight="4rem"
      mx={6}
      p={2}
    >
      <Text color="whiteAlpha.600" fontSize="md" lineHeight={1}>
        {message.length === 0
          ? 'No announcements yet'
          : `"${message.substring(0, 80)}${message.length > 80 ? '...' : ''}"`}
      </Text>
    </Box>
  );
};

export function agentContactInfoFromListing(listing?: {
  listingAgent?: {
    user: Partial<Pick<UserObject, 'fullName' | 'email' | 'phoneNumber'>>;
  };
  mlsListing?: {
    agent?: Partial<MlsListingAgentObject>;
    compliance?: Partial<MlsListingComplianceObject>;
  };
}) {
  if (!listing) {
    return {};
  }

  let phoneNumber: string | undefined = undefined;
  let email: string | undefined = undefined;

  // We don't know if the attribution contact is an email or a phone number, so we check based on the phone numbers and emails we have.
  const attributionContact = listing.mlsListing?.compliance?.attributionContact;

  if (attributionContact) {
    if (checkIsValidPhoneNumber(attributionContact)) {
      phoneNumber = attributionContact;
    } else if (attributionContact.includes('@')) {
      email = attributionContact;
    }
  } else {
    phoneNumber =
      listing.listingAgent?.user.phoneNumber ||
      listing.mlsListing?.agent?.contact?.cell ||
      listing.mlsListing?.agent?.contact?.office;
    email =
      listing.listingAgent?.user?.email ||
      listing.mlsListing?.agent?.contact?.email;
  }

  const fullName =
    listing.listingAgent?.user.fullName ??
    (listing.mlsListing?.agent?.firstName &&
      listing.mlsListing?.agent?.lastName)
      ? `${listing.mlsListing?.agent?.firstName as string} ${
          listing.mlsListing?.agent?.lastName as string
        }`
      : '';

  return { fullName, email, phoneNumber, attributionContact };
}
